import type { Session, ChannelConfig, ChannelConfigUpdate, UpdateChannelResponse, ApiError } from '../types'

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'https://ws-preprod-api.eng.toasttab.com/repo-context-bot'

// ── Session storage ────────────────────────────────────────────────────────

const SESSION_KEY = 'review_ops_session'

export function getSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const session: Session = JSON.parse(raw)
    if (Date.now() >= session.expiresAt) {
      localStorage.removeItem(SESSION_KEY)
      return null
    }
    return session
  } catch {
    return null
  }
}

export function saveSession(session: Session): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session))
}

export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY)
}

// ── HTTP primitives ────────────────────────────────────────────────────────

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  token?: string,
): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) headers['X-Review-Ops-Token'] = token

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body != null ? JSON.stringify(body) : undefined,
  })

  const text = await res.text()
  const json = text ? JSON.parse(text) : null

  if (!res.ok) {
    const err: ApiError = {
      status: res.status,
      message: json?.message ?? `HTTP ${res.status}`,
      field: json?.fieldName ?? json?.field,
    }
    throw err
  }

  return json as T
}

// ── Auth ──────────────────────────────────────────────────────────────────

export async function exchangeToken(token: string): Promise<Session> {
  return request<Session>('POST', '/api/v1/auth/exchange', { token })
}

// ── Channels ──────────────────────────────────────────────────────────────

export async function listChannels(sessionToken: string): Promise<ChannelConfig[]> {
  return request<ChannelConfig[]>('GET', '/api/v1/channels', undefined, sessionToken)
}

export async function getChannel(sessionToken: string, channelId: string): Promise<ChannelConfig> {
  return request<ChannelConfig>('GET', `/api/v1/channels/${channelId}`, undefined, sessionToken)
}

export async function updateChannel(
  sessionToken: string,
  channelId: string,
  update: ChannelConfigUpdate,
): Promise<UpdateChannelResponse> {
  return request<UpdateChannelResponse>('PUT', `/api/v1/channels/${channelId}`, update, sessionToken)
}

export async function deleteChannel(
  sessionToken: string,
  channelId: string,
): Promise<void> {
  return request<void>('DELETE', `/api/v1/channels/${channelId}`, undefined, sessionToken)
}

export async function provisionUserGroup(
  sessionToken: string,
  channelId: string,
  prefix: string,
  authorTeamSlug?: string,
): Promise<{ prUrl: string }> {
  const body: Record<string, string> = { prefix }
  if (authorTeamSlug) body.authorTeamSlug = authorTeamSlug
  return request<{ prUrl: string }>('POST', `/api/v1/channels/${channelId}/provision-usergroup`, body, sessionToken)
}
